import { Card, CardProps, Grid, GridDirection, makeStyles, Theme } from "@material-ui/core";
import React, { FunctionComponent } from "react";
import { isIOS } from "react-device-detect";

const POINTER_WIDTH = 55;
const POINTER_HEIGHT = 60;
const CARD_BORDER_RADIUS = 40;
const CARD_SHADOW = "0px 0px 40px #00000018";

interface ICardWithArrowStyleProps {
  isIOS: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    filter: ({ isIOS }: ICardWithArrowStyleProps) => (isIOS ? "" : `drop-shadow(${CARD_SHADOW})`),
    width: "100%",
  },
  cardContainer: {
    width: `100%`,
    borderRadius: CARD_BORDER_RADIUS,
    boxShadow: ({ isIOS }: ICardWithArrowStyleProps) => (isIOS ? CARD_SHADOW : ``),
  },
  cardContainerWithArrow: {
    width: `calc(100% - ${POINTER_HEIGHT}px)`,
  },
  pointerContainer: {
    width: `calc(100% - ${CARD_BORDER_RADIUS * 2}px)`,
    clipPath: "inset(-5px 0px -5px -5px)",
    zIndex: 10,
    "&.arrow-position-top": {
      marginLeft: CARD_BORDER_RADIUS,
      marginRight: CARD_BORDER_RADIUS,
    },
    "&.arrow-position-left": {
      marginTop: CARD_BORDER_RADIUS,
      marginBottom: CARD_BORDER_RADIUS,
      marginLeft: -3,
      width: POINTER_WIDTH,
    },
    "&.arrow-position-right": {
      marginTop: CARD_BORDER_RADIUS,
      marginBottom: CARD_BORDER_RADIUS,
      marginLeft: -3,
      width: POINTER_WIDTH,
    },
    "&.arrow-position-bottom": {
      marginLeft: CARD_BORDER_RADIUS,
      marginRight: CARD_BORDER_RADIUS,
    },
  },
  pointer: {
    backgroundColor: theme.palette.background.paper,
    width: POINTER_HEIGHT,
    height: POINTER_WIDTH,
    clipPath: "polygon(50% 0, 100% 100%, 0 100%)",
    "&.arrow-position-left": {
      marginLeft: -3,
      transform: "rotate(-90deg)",
    },
    "&.arrow-position-right": {
      transform: "rotate(90deg)",
    },
    "&.arrow-position-bottom": {
      transform: "rotate(180deg)",
    },
  },

  componentWrapper: {
    padding: theme.spacing(10),
    width: "100%",
    display: "flex",
    borderRadius: CARD_BORDER_RADIUS,
    minHeight: 100,
    marginLeft: -1,
  },
}));

export type CardArrowDirection = "top" | "bottom" | "left" | "right";
export type CardArrowPosition = "start" | "center" | "end";

interface IOnboardingCardWithArrowProps extends CardProps {
  showArrow: boolean;
  arrowDirection?: CardArrowDirection;
  arrowPosition?: CardArrowPosition;
}

const directions = {
  top: "column",
  left: "row",
  right: "row-reverse",
  bottom: "column-reverse",
};
const arrowDirections = {
  top: "row",
  left: "column",
  right: "column",
  bottom: "row",
};

export const CardWithArrow: FunctionComponent<IOnboardingCardWithArrowProps> = (props) => {
  const classes = useStyles({ isIOS });
  const { arrowDirection, arrowPosition, showArrow, ...rest } = props;

  const flexDirection: GridDirection = directions[arrowDirection ? arrowDirection : "row"];
  const adjustWidthForArrow = showArrow && (arrowDirection === "left" || arrowDirection === "right");

  const arrowContainerFlexDirection = arrowDirections[arrowDirection ? arrowDirection : "row"];

  return (
    <Grid container className={classes.container} direction={flexDirection} alignContent={"stretch"}>
      {showArrow && (
        <Grid
          item
          container
          className={`${classes.pointerContainer} arrow-position-${arrowDirection}`}
          direction={arrowContainerFlexDirection}
          justify={arrowPosition === "start" ? "flex-start" : arrowPosition === "center" ? "center" : "flex-end"}
          alignItems={arrowPosition === "start" ? "flex-start" : arrowPosition === "center" ? "center" : "flex-end"}
        >
          <Grid className={`${classes.pointer} arrow-position-${arrowDirection}`}></Grid>
        </Grid>
      )}
      <Grid container item className={adjustWidthForArrow ? classes.cardContainerWithArrow : classes.cardContainer}>
        <Card elevation={0} {...rest} className={`${props.className} ${classes.componentWrapper}`}>
          {props.children}
        </Card>
      </Grid>
    </Grid>
  );
};
